import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getDesktopEnv } from "../desktopEnv";
import { commandBus } from "../commands/CommandBus";
import { registerCoreCommands } from "../commands/registerCoreCommands";
import { ShortcutManager } from "../shortcuts/ShortcutManager";
import { defaultShortcutProfile } from "../shortcuts/profiles/defaultProfile";
import { DesktopKeyboardContext } from "./DesktopKeyboardContext";
import { desktopBus, DesktopEvents } from "../commands/EventBus";
import UniversalSearch from "../ui/UniversalSearch";
import CommandPalette from "../ui/CommandPalette";
import SidebarHintOverlay from "../ui/SidebarHintOverlay";
import { BarcodeScannerService } from "../barcode/BarcodeScannerService";
import { focusManager } from "../focus/FocusManager";
import { universalSearchService } from "../search/UniversalSearchService";
import "../popup/popupKeyboard";
import "../ui/desktopKeyboard.css";

function isEditableTarget(event: KeyboardEvent) {
  const target = event.target as HTMLElement | null;
  if (!target) return false;
  const tag = target.tagName?.toLowerCase();
  return (
    tag === "input" ||
    tag === "textarea" ||
    tag === "select" ||
    target.isContentEditable
  );
}

export default function DesktopKeyboardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const desktop = useMemo(() => getDesktopEnv(), []);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showSidebarHints, setShowSidebarHints] = useState(false);
  const shortcuts = useMemo(
    () => (desktop.isDesktop ? new ShortcutManager(commandBus) : null),
    [desktop.isDesktop],
  );
  const barcodeScanner = useMemo(
    () => (desktop.isDesktop ? new BarcodeScannerService(commandBus) : null),
    [desktop.isDesktop],
  );

  useEffect(() => {
    if (!desktop.isDesktop) return;
    universalSearchService.setItems([
      { id: "page-dashboard", label: "Dashboard", type: "page", commandId: "nav.dashboard" },
      { id: "page-sales", label: "Sales / Invoice", type: "page", commandId: "nav.sales" },
      { id: "page-purchase", label: "Purchase", type: "page", commandId: "nav.purchase" },
      { id: "page-products", label: "Products", type: "page", commandId: "nav.products" },
      { id: "page-customers", label: "Customers", type: "page", commandId: "nav.customers" },
      { id: "page-reports", label: "Reports", type: "page", commandId: "nav.reports" },
    ]);
  }, [desktop.isDesktop]);

  useEffect(() => {
    if (!desktop.isDesktop || !shortcuts) return undefined;
    shortcuts.setScope(`page:${location.pathname}`);
  }, [desktop.isDesktop, shortcuts, location.pathname]);

  useEffect(() => {
    if (!desktop.isDesktop || !shortcuts) return undefined;
    const unregisterCore = registerCoreCommands(commandBus, { navigate });
    const unregisterShortcuts = defaultShortcutProfile.map((binding) =>
      shortcuts.register(binding),
    );

    const unsubPalette = desktopBus.on(DesktopEvents.PALETTE_TOGGLE, () => {
      setPaletteOpen(true);
    });
    const unsubSearch = desktopBus.on(DesktopEvents.SEARCH_TOGGLE, () => {
      setSearchOpen(true);
    });
    const unsubHints = desktopBus.on<{ visible: boolean }>(
      DesktopEvents.SIDEBAR_HINTS,
      (payload) => setShowSidebarHints(Boolean(payload?.visible)),
    );

    return () => {
      unregisterCore();
      unregisterShortcuts.forEach((unregister) => unregister());
      unsubPalette();
      unsubSearch();
      unsubHints();
    };
  }, [desktop.isDesktop, shortcuts, navigate]);

  useEffect(() => {
    if (!desktop.isDesktop || !shortcuts) return undefined;
    barcodeScanner?.setEnabled(true);

    const onKeyDown = async (event: KeyboardEvent) => {
      if (event.key === "Enter" && !event.ctrlKey && !event.metaKey && !event.altKey) {
        if (event.shiftKey) {
          if (focusManager.focusPrev()) event.preventDefault();
        } else if (focusManager.focusNext()) {
          event.preventDefault();
        }
      }
      if (event.key === "Tab" && !event.ctrlKey && !event.metaKey && !event.altKey) {
        if (focusManager.focusNextGroup()) event.preventDefault();
      }

      if (event.key === "Alt") {
        setShowSidebarHints(true);
        shortcuts.setScope("sidebarHints");
        return;
      }

      if (isEditableTarget(event)) {
        const protectedCombos = ["k", "p"];
        if ((event.ctrlKey || event.metaKey) && protectedCombos.includes(event.key.toLowerCase())) {
          // allowed in editable when bound
        } else if (event.key.length === 1) {
          barcodeScanner?.handleKeyDown(event);
          return;
        }
      }

      barcodeScanner?.handleKeyDown(event);
      await shortcuts.handleKeyDown(event, { route: location.pathname });
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Alt") {
        setShowSidebarHints(false);
        shortcuts.setScope(`page:${location.pathname}`);
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
    };
  }, [desktop.isDesktop, shortcuts, barcodeScanner, location.pathname]);

  useEffect(() => {
    if (desktop.isDesktop) {
      document.documentElement.setAttribute("data-vedas-desktop", "true");
      return () => {
        document.documentElement.removeAttribute("data-vedas-desktop");
      };
    }
    return undefined;
  }, [desktop.isDesktop]);

  if (!desktop.isDesktop || !shortcuts) {
    return <>{children}</>;
  }

  return (
    <DesktopKeyboardContext.Provider value={{ desktop, commandBus, shortcuts }}>
      {children}
      <UniversalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <SidebarHintOverlay visible={showSidebarHints} />
    </DesktopKeyboardContext.Provider>
  );
}
